import React from 'react';
import classNames from 'classnames';

import {Icon, Text} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

export type AccountType = 'educator' | 'student';

interface AccountTypeCardProps {
  title: string;
  subtitle?: string;
  icon: IconProp;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

function AccountTypeCard({
  title,
  subtitle,
  icon,
  selected,
  onClick,
  disabled
}: AccountTypeCardProps) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      disabled={disabled}
      className={classNames(
        'flex w-full items-center gap-x2 rounded-full py-x1-half pr-x3 pl-x2 transition-colors m:max-w-[432px] m:py-x2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        {
          'cursor-pointer': !disabled,
          'border-2 border-selected background-brand-subtle-hover': selected,
          'border border-primary background-primary': !selected,
          'hover:background-subtle': !selected && !disabled
        }
      )}
      onClick={onClick}
    >
      <div className='flex h-x6-quarter w-x6-quarter shrink-0 items-center justify-center rounded-full background-brand'>
        <Icon icon={icon} className='text-xl content-primary-inverse' />
      </div>
      <div className='flex flex-1 flex-col items-start justify-center gap-x0-half text-left'>
        <Text color='primary' bold size='xl'>
          {title}
        </Text>
        {subtitle && (
          <Text size='s' color='secondary'>
            {subtitle}
          </Text>
        )}
      </div>
      <Icon icon='chevron-right' />
    </button>
  );
}

interface AccountTypeSelectorProps {
  selectedType: AccountType | null;
  onSelect: (type: AccountType) => void;
  disabled?: boolean;
}

export default function AccountTypeSelector({
  selectedType,
  onSelect,
  disabled
}: AccountTypeSelectorProps) {
  return (
    <div className='flex w-full flex-col items-center gap-x1-half'>
      <AccountTypeCard
        title='Educator'
        subtitle='K-12, Homeschool Educators, and Tutors'
        icon={['far', 'chalkboard-user']}
        selected={selectedType === 'educator'}
        onClick={() => onSelect('educator')}
        disabled={disabled}
      />
      <AccountTypeCard
        title='Student'
        icon={['far', 'backpack']}
        selected={selectedType === 'student'}
        onClick={() => onSelect('student')}
        disabled={disabled}
      />
    </div>
  );
}
